import React, { useState, useEffect } from 'react';
import axios from 'axios';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { formatDateFrench, formatPeriodFrench } from '../utils/dateUtils';
import { toast } from 'sonner';
import { BilanSkeleton } from './ui/Skeleton';
import { useAppConfig } from '../context/AppConfigContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BilanRecap = () => {
  const { appConfig } = useAppConfig();
  const companyName = appConfig?.company_name || null;
  const [bilan, setBilan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filterEmployee, setFilterEmployee] = useState('');
  const [filterClient, setFilterClient] = useState('');
  const [filterOwner, setFilterOwner] = useState('');
  const [employees, setEmployees] = useState([]);
  const [clients, setClients] = useState([]);
  const [housings, setHousings] = useState([]);
  const [totalGeneralFacturation, setTotalGeneralFacturation] = useState(0);
  
  // Détails facturation par catégorie
  const [detailsFacturation, setDetailsFacturation] = useState({
    menages: 0,
    parures: 0,
    nbParures: 0,
    consommables: 0,
    prestations: 0
  });
  
  // Sélecteur de mois (comme dans Facturation)
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (employees.length > 0) {
      loadBilan();
    }
  }, [selectedMonth, selectedYear, filterEmployee, filterClient, filterOwner]);

  const loadData = async () => {
    try {
      const [employeesRes, clientsRes, housingsRes] = await Promise.all([
        axios.get(`${API}/employees`),
        axios.get(`${API}/clients`),
        axios.get(`${API}/housings`)
      ]);
      setEmployees(employeesRes.data);
      setClients(clientsRes.data);
      setHousings(housingsRes.data);
      await loadBilan();
    } catch (error) {
      console.error("Erreur chargement données:", error);
    }
  };

  const loadBilan = async () => {
    setLoading(true);
    try {
      // Calculer les dates de début et fin du mois sélectionné
      const startDate = `${selectedYear}-${String(selectedMonth).padStart(2, '0')}-01`;
      const lastDay = new Date(selectedYear, selectedMonth, 0).getDate();
      const endDate = `${selectedYear}-${String(selectedMonth).padStart(2, '0')}-${lastDay}`;
      
      // Le bilan charge maintenant TOUTES les missions (sans filtre owner)
      // et retourne les totaux par gestion dans "par_gestion"
      let url = `${API}/bilan?start_date=${startDate}&end_date=${endDate}`;
      if (filterEmployee) url += `&employee_id=${filterEmployee}`;
      if (filterClient) url += `&client_id_filter=${filterClient}`;
      // Ne plus filtrer par owner car on veut voir toutes les missions avec leurs badges
      
      const response = await axios.get(url);
      setBilan(response.data);
      
      // Charger la facturation totale (toutes gestions regroupées, dynamique par tenant)
      try {
        // Récupérer la liste des gestionnaires du tenant (dynamique)
        let proprietaires = {};
        try {
          const propRes = await axios.get(`${API}/proprietaires-config`);
          proprietaires = propRes.data || {};
        } catch (e) { /* pas de propriétaires : on ne prend que la gestion principale */ }
        
        // 1 requête pour la gestion principale (sans propriétaire) + 1 par propriétaire
        const facturationRequests = [
          axios.get(`${API}/facturation/monthly-recap/${selectedYear}/${selectedMonth}`),
          ...Object.keys(proprietaires).map(slug =>
            axios.get(`${API}/facturation/monthly-recap/${selectedYear}/${selectedMonth}?owner_filter=${encodeURIComponent(slug)}`)
          ),
        ];
        const facturationResponses = await Promise.all(facturationRequests);
        
        // Charger les ajustements manuels de facturation (parures modifiées, etc.)
        let savedEdits = {};
        try {
          const editsRes = await axios.get(`${API}/facturation/status/${selectedYear}/${selectedMonth}`);
          savedEdits = editsRes.data.edits || {};
        } catch (e) { /* pas d'ajustements */ }
        
        // Appliquer les ajustements et calculer le total
        const applyEdits = (factData) => {
          const clients = factData.clients || [];
          let totalAjuste = 0;
          clients.forEach(client => {
            (client.logements || []).forEach(log => {
              const editKey = `${client.client_id || client.client_name}_${log.housing_id}`;
              if (savedEdits[editKey]) {
                Object.assign(log, savedEdits[editKey]);
              }
              totalAjuste += parseFloat(log.total_logement || 0);
            });
          });
          return { clients, totalAjuste };
        };
        
        const gestionResults = facturationResponses.map(r => ({
          raw: r.data,
          applied: applyEdits(r.data),
        }));
        
        // CA TOTAL = somme de toutes les gestions (principale + propriétaires)
        const caTotal = gestionResults.reduce((sum, g) =>
          sum + (g.applied.totalAjuste || parseFloat(g.raw.total_general) || 0), 0
        );
        setTotalGeneralFacturation(caTotal);
        
        // Détails par catégorie (somme de tous les flux)
        let menages = 0, parures = 0, nbParures = 0, consommables = 0, prestations = 0;
        gestionResults.forEach(({ applied }) => {
          applied.clients.forEach(client => {
            (client.logements || []).forEach(log => {
              menages += parseFloat(log.prix_menages_total || 0);
              parures += parseFloat(log.tarif_parures_total || 0);
              nbParures += parseInt(log.nb_parures_total || 0);
              consommables += parseFloat(log.total_consommables || 0);
              prestations += parseFloat(log.total_prestations || 0);
            });
          });
        });
        
        setDetailsFacturation({ menages, parures, nbParures, consommables, prestations });
      } catch (factError) {
        console.log("Impossible de charger les totaux facturation:", factError);
        setTotalGeneralFacturation(0);
      }
      
    } catch (error) {
      console.error("Erreur chargement bilan:", error);
      toast.info("Erreur lors du chargement du bilan");
    } finally {
      setLoading(false);
    }
  };

  const getRatioColor = (ratio) => {
    if (ratio >= 120) return 'bg-green-500 text-white';
    if (ratio >= 100) return 'bg-lime-500 text-white';
    if (ratio >= 80) return 'bg-orange-500 text-white';
    return 'bg-red-500 text-white';
  };

  const getMargeColor = (marge) => {
    return marge >= 0 ? 'text-green-600' : 'text-red-600';
  };

  // Fonction export PDF
  const exportToPDF = () => {
    if (!bilan || !bilan.par_employe || !bilan.synthese) {
      toast.error("Aucune donnée à exporter. Veuillez charger le bilan d\'abord.");
      return;
    }
    
    try {
      const doc = new jsPDF();
      
      // Calculs pour le header basés sur type_employe
      const proprietaires = bilan.par_employe.filter(emp => emp.type_employe === 'proprietaire');
      const intervenants = bilan.par_employe.filter(emp => emp.type_employe !== 'proprietaire');
      const coutIntervenants = intervenants.reduce((sum, emp) => sum + emp.total_paye, 0);
      const gainsProprietaires = proprietaires.reduce((sum, emp) => sum + emp.chiffre_genere, 0);
      const chiffreAffaires = totalGeneralFacturation > 0 ? totalGeneralFacturation : bilan.synthese.total_facturation_client;
      const margeNette = chiffreAffaires - coutIntervenants;
      const ratio = coutIntervenants > 0 ? Math.round((chiffreAffaires / coutIntervenants) * 100) : 0;

    // Titre
    doc.setFontSize(20);
    doc.setFont('helvetica', 'bold');
    doc.text(companyName ? `${companyName} - Bilan de Performance` : 'Bilan de Performance', 105, 20, { align: 'center' });
    
    // Période
    doc.setFontSize(12);
    doc.setFont('helvetica', 'normal');
    const periodeText = `Période: ${new Date(bilan.periode.start_date).toLocaleDateString('fr-FR')} - ${new Date(bilan.periode.end_date).toLocaleDateString('fr-FR')}`;
    doc.text(periodeText, 105, 30, { align: 'center' });
    
    // Synthèse
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('Synthèse Financière', 14, 45);
    
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    let yPos = 55;
    doc.text(`Chiffre d'Affaires du Mois: ${chiffreAffaires.toFixed(2)}€`, 14, yPos);
    doc.text(`Facturation aux Clients: ${bilan.synthese.total_facturation_client.toFixed(2)}€`, 14, yPos + 7);
    doc.text(`Gains Propriétaires: ${gainsProprietaires.toFixed(2)}€`, 14, yPos + 14);
    doc.text(`Coût Intervenants: ${coutIntervenants.toFixed(2)}€`, 14, yPos + 21);
    doc.text(`Marge Nette: ${margeNette.toFixed(2)}€`, 14, yPos + 28);
    doc.text(`Ratio Rentabilité: ${ratio}%`, 14, yPos + 35);
    
    // Tableau Performance par Employé
    yPos = 100;
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('Performance par Employé', 14, yPos);
    
    const employeeData = bilan.par_employe.map(emp => [
      emp.employee_name,
      emp.nb_missions,
      emp.total_paye.toFixed(2) + '€',
      emp.chiffre_genere.toFixed(2) + '€',
      emp.marge.toFixed(2) + '€',
      emp.ratio + '%'
    ]);
    
    autoTable(doc, {
      startY: yPos + 5,
      head: [['Employé', 'Missions', 'Prix', 'Généré', 'Marge', 'Ratio']],
      body: employeeData,
      theme: 'striped',
      headStyles: { fillColor: [59, 130, 246] },
      styles: { fontSize: 9 }
    });
    
    // Tableau Performance par Client
    yPos = doc.lastAutoTable.finalY + 15;
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('Performance par Client', 14, yPos);
    
    const clientData = bilan.par_client.slice(0, 15).map(client => [
      client.client_name,
      client.nb_missions,
      client.total_facture + '€',
      client.cout_employe + '€',
      client.marge + '€',
      client.marge_moyenne + '€'
    ]);
    
    autoTable(doc, {
      startY: yPos + 5,
      head: [['Client', 'Missions', 'Facturé', 'Coût', 'Marge Tot.', 'Marge Moy.']],
      body: clientData,
      theme: 'striped',
      headStyles: { fillColor: [34, 197, 94] },
      styles: { fontSize: 8 }
    });
    
      // Sauvegarder
      const filename = `bilan_${new Date(bilan.periode.start_date).toLocaleDateString('fr-FR').replace(/\//g, '-')}_au_${new Date(bilan.periode.end_date).toLocaleDateString('fr-FR').replace(/\//g, '-')}.pdf`;
      doc.save(filename);
      
      toast.success("PDF généré avec succès !");
    } catch (error) {
      console.error('Erreur génération PDF:', error);
      toast.error(`Erreur lors de la génération du PDF: ${error.message}`);
    }
  };

  // Fonction export CSV pour Google Sheets
  const exportToCSV = () => {
    // Calculs basés sur type_employe
    const proprietaires = bilan.par_employe.filter(emp => emp.type_employe === 'proprietaire');
    const intervenants = bilan.par_employe.filter(emp => emp.type_employe !== 'proprietaire');
    const coutIntervenants = intervenants.reduce((sum, emp) => sum + emp.total_paye, 0);
    const gainsProprietaires = proprietaires.reduce((sum, emp) => sum + emp.chiffre_genere, 0);
    const chiffreAffaires = totalGeneralFacturation > 0 ? totalGeneralFacturation : bilan.synthese.total_facturation_client;
    const margeNette = chiffreAffaires - coutIntervenants;
    const ratio = coutIntervenants > 0 ? Math.round((chiffreAffaires / coutIntervenants) * 100) : 0;

    let csv = 'Bilan de Performance\n\n';
    csv += `Période,${new Date(bilan.periode.start_date).toLocaleDateString('fr-FR')},${new Date(bilan.periode.end_date).toLocaleDateString('fr-FR')}\n\n`;
    
    csv += 'SYNTHÈSE FINANCIÈRE\n';
    csv += `Chiffre d'Affaires du Mois,${chiffreAffaires.toFixed(2)}€\n`;
    csv += `Facturation aux Clients,${bilan.synthese.total_facturation_client.toFixed(2)}€\n`;
    csv += `Gains Propriétaires,${gainsProprietaires.toFixed(2)}€\n`;
    csv += `Coût Intervenants,${coutIntervenants.toFixed(2)}€\n`;
    csv += `Marge Nette,${margeNette.toFixed(2)}€\n`;
    csv += `Ratio Rentabilité,${ratio}%\n\n`;
    
    csv += 'PERFORMANCE PAR EMPLOYÉ\n';
    csv += 'Employé,Type,Missions,Prix,Généré,Marge,Ratio\n';
    bilan.par_employe.forEach(emp => {
      const typeLabel = emp.type_employe === 'proprietaire' ? 'Propriétaire' : 'Intervenant';
      csv += `${emp.employee_name},${typeLabel},${emp.nb_missions},${emp.total_paye.toFixed(2)}€,${emp.chiffre_genere.toFixed(2)}€,${emp.marge.toFixed(2)}€,${emp.ratio}%\n`;
    });
    
    csv += '\nPERFORMANCE PAR CLIENT\n';
    csv += 'Client,Missions,Facturé,Coût,Marge Totale,Marge Moyenne\n';
    bilan.par_client.forEach(client => {
      csv += `${client.client_name},${client.nb_missions},${client.total_facture}€,${client.cout_employe}€,${client.marge}€,${client.marge_moyenne}€\n`;
    });
    
    csv += '\nTOP LOGEMENTS PAR MARGE\n';
    csv += 'Client,Logement,Missions,Généré,Coût,Marge\n';
    bilan.par_logement.forEach(housing => {
      csv += `${housing.client_name},${housing.housing_name},${housing.nb_missions},${housing.chiffre_genere}€,${housing.cout_employe}€,${housing.marge}€\n`;
    });
    
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    const filename = `bilan_${new Date(bilan.periode.start_date).toLocaleDateString('fr-FR').replace(/\//g, '-')}_au_${new Date(bilan.periode.end_date).toLocaleDateString('fr-FR').replace(/\//g, '-')}.csv`;
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto py-6 px-4" data-testid="bilan-loading">
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">📊 Bilan de Performance</h1>
          <p className="text-gray-600">Chargement...</p>
        </div>
        <BilanSkeleton />
      </div>
    );
  }

  if (!bilan) return null;

  return (
    <div className="max-w-7xl mx-auto py-6 px-4">
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">📊 Bilan de Performance</h1>
          <p className="text-gray-600">
            Vue d'ensemble de la rentabilité et des performances
          </p>
        </div>
        
        {/* Boutons d'export */}
        <div className="flex items-center space-x-3">
          <button
            onClick={exportToPDF}
            disabled={!bilan || !bilan.par_employe}
            className="inline-flex items-center px-4 py-2 bg-red-600 hover:bg-red-700 text-white font-medium rounded-lg shadow-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
            </svg>
            Exporter PDF
          </button>
          
          <button
            onClick={exportToCSV}
            className="inline-flex items-center px-4 py-2 bg-green-600 hover:bg-green-700 text-white font-medium rounded-lg shadow-md transition-colors"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            Exporter Google Sheets
          </button>
        </div>
      </div>

      {/* Titre Bilan */}
      <div className="mb-6">
        <h2 className="text-lg font-bold text-gray-800">📊 Bilan de Performance{companyName ? ` ${companyName}` : ''}</h2>
      </div>

      {/* Période affichée en grand avec design premium */}
      <div className="mb-8 text-center">
        <div className="inline-block px-8 py-4 bg-gradient-to-r from-slate-800 via-slate-900 to-slate-800 rounded-2xl shadow-2xl border border-slate-700">
          <div className="text-base font-semibold text-slate-200 uppercase tracking-wider mb-2" style={{ letterSpacing: '0.15em' }}>
            Période
          </div>
          <div className="text-4xl font-semibold text-transparent bg-clip-text bg-gradient-to-r from-amber-400 via-amber-300 to-amber-400 tracking-tight" style={{ 
            letterSpacing: '-0.015em',
            fontWeight: '600'
          }}>
            {['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'][selectedMonth - 1]} {selectedYear}
          </div>
        </div>
      </div>

      {/* Filtres */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Mois</label>
            <select
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
              className="form-select w-full"
            >
              <option value={1}>Janvier</option>
              <option value={2}>Février</option>
              <option value={3}>Mars</option>
              <option value={4}>Avril</option>
              <option value={5}>Mai</option>
              <option value={6}>Juin</option>
              <option value={7}>Juillet</option>
              <option value={8}>Août</option>
              <option value={9}>Septembre</option>
              <option value={10}>Octobre</option>
              <option value={11}>Novembre</option>
              <option value={12}>Décembre</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Année</label>
            <select
              value={selectedYear}
              onChange={(e) => setSelectedYear(parseInt(e.target.value))}
              className="form-select w-full"
            >
              {[2024, 2025, 2026, 2027].map(year => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Employé</label>
            <select
              value={filterEmployee}
              onChange={(e) => setFilterEmployee(e.target.value)}
              className="form-select w-full"
            >
              <option value="">Tous les employés</option>
              {employees.map(emp => (
                <option key={emp.id} value={emp.id}>
                  {emp.prenom} {emp.nom}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Client</label>
            <select
              value={filterClient}
              onChange={(e) => setFilterClient(e.target.value)}
              className="form-select w-full"
            >
              <option value="">Tous les clients</option>
              {clients.map(client => (
                <option key={client.id} value={client.id}>
                  {client.prenom} {client.nom}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Cartes métriques synthèse — vue simplifiée : CA total, Coût intervenants, Bénéfice net */}
      {(() => {
        const proprietaires = bilan.par_employe.filter(emp => emp.type_employe === 'proprietaire');
        const intervenants = bilan.par_employe.filter(emp => emp.type_employe !== 'proprietaire');
        
        // Données globales par gestion (agrégées côté backend — dynamique par slug)
        const parGestion = bilan.par_gestion || {};
        const gestionValues = Object.values(parGestion);
        
        // CA total = facturation cumulée (toutes gestions)
        const caTotal = totalGeneralFacturation ||
          (detailsFacturation.menages + detailsFacturation.parures + detailsFacturation.consommables + detailsFacturation.prestations);
        
        // Missions totales et coût total = somme de toutes les gestions
        const missionsTotal = gestionValues.reduce((s, g) => s + (g.missions || 0), 0);
        const coutTotal = gestionValues.reduce((s, g) => s + (g.cout || 0), 0);
        
        // Coût gérants (propriétaires) — à exclure du coût des intervenants externes
        const coutGerants = proprietaires.reduce((sum, emp) => sum + emp.total_paye, 0);
        const missionsProprietaires = proprietaires.reduce((sum, emp) => sum + emp.nb_missions, 0);
        
        const coutIntervenantsExternes = Math.max(0, coutTotal - coutGerants);
        const nbMissionsIntervenants = Math.max(0, missionsTotal - missionsProprietaires);
        
        // Bénéfice net = CA total - Coût intervenants externes
        const beneficeNet = caTotal - coutIntervenantsExternes;
        
        return (
          <div className="mb-6 space-y-4">
            {/* CHIFFRE D'AFFAIRES TOTAL */}
            <div className="bg-gradient-to-br from-emerald-500 to-emerald-600 text-white rounded-xl p-6 shadow-xl" data-testid="bilan-ca-total">
              <div className="text-sm font-semibold uppercase tracking-wider opacity-90 mb-2">💰 CHIFFRE D'AFFAIRES TOTAL</div>
              <div className="text-xs italic opacity-90 mb-3">= tout ce que vos clients vous ont payé</div>
              <div className="text-5xl font-bold mb-2">{caTotal.toFixed(2)}€</div>
              <div className="text-sm opacity-80">{missionsTotal} missions — toutes gestions regroupées</div>
              <a
                href="/facturation"
                className="inline-block mt-3 text-xs bg-white/20 hover:bg-white/30 px-3 py-1.5 rounded transition-colors"
              >
                Voir la facturation détaillée →
              </a>
            </div>

            {/* COÛT INTERVENANTS */}
            <div className="bg-gradient-to-br from-orange-500 to-orange-600 text-white rounded-xl p-5 shadow-xl" data-testid="bilan-cout-intervenants">
              <div className="text-sm font-semibold uppercase tracking-wider opacity-90 mb-2">💸 COÛT DES INTERVENANTS</div>
              <div className="text-xs italic opacity-90 mb-3">= ce que vous avez versé à votre équipe</div>
              <div className="text-4xl font-bold mb-1">{coutIntervenantsExternes.toFixed(2)}€</div>
              <div className="text-sm opacity-80">{nbMissionsIntervenants} missions</div>
            </div>

            {/* BÉNÉFICE NET */}
            <div className={`rounded-xl p-6 shadow-2xl border-4 ${beneficeNet >= 0 ? 'bg-gradient-to-br from-green-600 to-green-700 border-green-300' : 'bg-gradient-to-br from-red-600 to-red-700 border-red-300'} text-white`} data-testid="bilan-benefice-net">
              <div className="text-sm font-semibold uppercase tracking-wider opacity-90 mb-2">📊 BÉNÉFICE NET</div>
              <div className="text-xs italic opacity-90 mb-3">= ce qu'il vous reste après avoir payé votre équipe</div>
              <div className="text-5xl font-bold mb-2">{beneficeNet.toFixed(2)}€</div>
              <div className="text-sm opacity-90">= CA ({caTotal.toFixed(2)}€) − Coût intervenants ({coutIntervenantsExternes.toFixed(2)}€)</div>
            </div>

            {/* BARRE DE DÉCOMPOSITION VISUELLE : CA = Coût intervenants + Bénéfice net */}
            {caTotal > 0 && (() => {
              const pctCout = Math.max(0, Math.min(100, (coutIntervenantsExternes / caTotal) * 100));
              const pctBenef = Math.max(0, 100 - pctCout);
              const isNegative = beneficeNet < 0;
              return (
                <div className="bg-white rounded-xl p-5 shadow-md border border-gray-200" data-testid="bilan-decomposition-bar">
                  <div className="text-sm font-bold text-gray-800 mb-2">🧩 Décomposition du CA</div>
                  <div className="text-xs text-gray-600 mb-3 italic">Visualisation simple : chaque euro facturé se répartit entre ce que vous payez à l'équipe et ce qui reste pour vous.</div>
                  <div className="w-full h-10 flex rounded-lg overflow-hidden shadow-inner bg-gray-100">
                    <div
                      className="bg-orange-500 flex items-center justify-center text-white text-xs font-bold transition-all"
                      style={{ width: `${pctCout}%` }}
                      title={`Coût intervenants : ${coutIntervenantsExternes.toFixed(2)}€`}
                    >
                      {pctCout >= 10 ? `${pctCout.toFixed(0)}%` : ''}
                    </div>
                    <div
                      className={`${isNegative ? 'bg-red-500' : 'bg-green-600'} flex items-center justify-center text-white text-xs font-bold transition-all`}
                      style={{ width: `${pctBenef}%` }}
                      title={`Bénéfice net : ${beneficeNet.toFixed(2)}€`}
                    >
                      {pctBenef >= 10 ? `${pctBenef.toFixed(0)}%` : ''}
                    </div>
                  </div>
                  <div className="flex justify-between mt-3 text-xs">
                    <div className="flex items-center gap-2">
                      <span className="inline-block w-3 h-3 bg-orange-500 rounded"></span>
                      <span className="text-gray-700"><span className="font-bold">Coût intervenants</span> {coutIntervenantsExternes.toFixed(2)}€</span>
                    </div>
                    <div className="text-gray-500 font-semibold">+</div>
                    <div className="flex items-center gap-2">
                      <span className={`inline-block w-3 h-3 rounded ${isNegative ? 'bg-red-500' : 'bg-green-600'}`}></span>
                      <span className="text-gray-700"><span className="font-bold">Bénéfice net</span> {beneficeNet.toFixed(2)}€</span>
                    </div>
                    <div className="text-gray-500 font-semibold">=</div>
                    <div className="flex items-center gap-2">
                      <span className="inline-block w-3 h-3 bg-emerald-500 rounded"></span>
                      <span className="text-gray-700"><span className="font-bold">CA Total</span> {caTotal.toFixed(2)}€</span>
                    </div>
                  </div>
                </div>
              );
            })()}

            {/* SECTION GÉRANT — missions effectuées par le gérant lui-même */}
            {missionsProprietaires > 0 && (
              <div className="bg-gradient-to-br from-amber-50 to-amber-100 border-l-4 border-amber-400 rounded-xl p-5 shadow-sm" data-testid="bilan-section-gerant">
                <div className="flex items-start gap-3">
                  <div className="text-3xl">👤</div>
                  <div className="flex-1">
                    <div className="text-sm font-bold uppercase tracking-wider text-amber-900 mb-2">Vos missions en direct</div>
                    <div className="text-base text-gray-800 leading-relaxed">
                      Vous avez effectué <span className="font-bold text-amber-800">{missionsProprietaires} mission{missionsProprietaires > 1 ? 's' : ''}</span> vous-même → vous avez perçu <span className="font-bold text-amber-800">{coutGerants.toFixed(2)}€</span> directement.
                    </div>
                    <div className="text-sm text-gray-600 mt-2 italic">
                      Ce montant est inclus dans le <span className="font-semibold">CA Total</span> et dans le <span className="font-semibold">Coût des intervenants</span> — il s'annule et <span className="font-bold underline">n'impacte pas votre Bénéfice net</span>.
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      })()}

      {/* Tableau par employé */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6 overflow-hidden">
        <div className="bg-gradient-to-r from-purple-50 to-purple-100 px-6 py-4 border-b border-gray-200">
          <h2 className="text-xl font-bold text-gray-900">👥 Performance par Employé</h2>
        </div>
        
        {/* Légende des termes */}
        <div className="bg-blue-50 px-6 py-3 border-b border-blue-200">
          <div className="text-xs text-gray-700 space-y-1">
            <div className="font-semibold text-blue-900 mb-2">📖 Légende des termes :</div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
              <div><span className="font-bold">Prix :</span> Ce que VOUS PAYEZ à l'employé</div>
              <div><span className="font-bold">Généré :</span> Ce que le CLIENT vous PAIE (CA)</div>
              <div><span className="font-bold">Marge :</span> Votre BÉNÉFICE (Généré - Prix)</div>
            </div>
            <div className="mt-2"><span className="font-bold">Ratio (Rentabilité) :</span> (Généré / Prix) x 100 → 🟢 Plus de 120% = Excellent | 🟡 100-120% = Bon | 🟠 80-100% = Moyen | 🔴 Moins de 80% = Faible</div>
          </div>
        </div>
        
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Employé</th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Missions</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Prix</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Généré</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Marge</th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Ratio</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {(() => {
                // Séparer Propriétaires, Intervenants et missions non attribuées par type_employe.
                // NOTE : toutes les missions comptent, quelle que soit la gestion (propriétaire assigné ou non).
                // sont comptées globalement puisque les gestions séparées ont été supprimées.
                const proprietaires = bilan.par_employe
                  .filter(emp => emp.type_employe === 'proprietaire');
                
                const unknownEmployee = bilan.par_employe.find(emp => 
                  emp.employee_name === 'Employé inconnu' ||
                  emp.employee_name.toLowerCase().includes('inconnu')
                );
                
                const intervenants = bilan.par_employe
                  .filter(emp => 
                    emp.type_employe !== 'proprietaire' &&
                    emp.employee_name !== 'Employé inconnu' &&
                    !emp.employee_name.toLowerCase().includes('inconnu') &&
                    emp.nb_missions > 0
                  );

                return (
                  <>
                    {/* Propriétaires en premier - Encadré rouge élégant */}
                    {proprietaires.map((proprietaire, idx) => (
                      <tr key={`prop-${idx}`} className="relative">
                        <td colSpan="6" className="p-0">
                          <div className="m-3 rounded-xl border-2 border-red-400 bg-gradient-to-r from-red-50 via-white to-red-50 shadow-lg overflow-hidden">
                            <div className="bg-gradient-to-r from-red-600 to-red-500 px-4 py-2">
                              <span className="text-white font-bold text-sm">🏠 {proprietaire.employee_name} • Gérant</span>
                            </div>
                            <table className="w-full">
                              <tbody>
                                <tr className="hover:bg-red-50/50">
                                  <td className="px-6 py-4 w-1/4">
                                    <div className="font-bold text-red-900 text-lg">{proprietaire.employee_name}</div>
                                  </td>
                                  <td className="px-6 py-4 text-center text-red-900 font-semibold w-1/6">{proprietaire.nb_missions} missions</td>
                                  <td className="px-6 py-4 text-right w-1/6">
                                    <div className="font-bold text-red-900 text-lg">{proprietaire.total_paye}€</div>
                                    <div className="text-xs text-red-600 font-medium">Prix Gérant</div>
                                  </td>
                                  <td className="px-6 py-4 text-right text-red-900 font-semibold w-1/6">{proprietaire.chiffre_genere}€</td>
                                  <td className={`px-6 py-4 text-right font-bold text-xl w-1/6 ${getMargeColor(proprietaire.marge)}`}>{proprietaire.marge}€</td>
                                  <td className="px-6 py-4 text-center w-1/6">
                                    <span className={`inline-block px-4 py-2 rounded-full text-sm font-bold ${getRatioColor(proprietaire.ratio)}`}>
                                      {proprietaire.ratio}%
                                    </span>
                                  </td>
                                </tr>
                              </tbody>
                            </table>
                          </div>
                        </td>
                      </tr>
                    ))}
                    
                    {/* Ligne de séparation */}
                    {proprietaires.length > 0 && (unknownEmployee || intervenants.length > 0) && (
                      <tr className="bg-gray-100">
                        <td colSpan="6" className="px-6 py-2 text-center text-xs text-gray-600 font-semibold uppercase tracking-wide">
                          👥 Intervenants Externes
                        </td>
                      </tr>
                    )}
                    
                    {/* Missions non attribuées */}
                    {unknownEmployee && (
                      <tr className="bg-orange-50 hover:bg-orange-100 border-b-2 border-orange-300">
                        <td className="px-6 py-4 font-bold italic text-orange-900 text-base">
                          Mission{unknownEmployee.nb_missions > 1 ? 's' : ''} programmée{unknownEmployee.nb_missions > 1 ? 's' : ''} non attribuée{unknownEmployee.nb_missions > 1 ? 's' : ''}
                        </td>
                        <td className="px-6 py-4 text-center text-orange-900 font-semibold">{unknownEmployee.nb_missions}</td>
                        <td className="px-6 py-4 text-right">
                          <div className="font-bold text-orange-900">{unknownEmployee.total_paye}€</div>
                          <div className="text-xs text-orange-600 font-medium">Prix Intervenant</div>
                        </td>
                        <td className="px-6 py-4 text-right text-orange-900 font-semibold">{unknownEmployee.chiffre_genere}€</td>
                        <td className={`px-6 py-4 text-right font-bold ${getMargeColor(unknownEmployee.marge)}`}>{unknownEmployee.marge}€</td>
                        <td className="px-6 py-4 text-center">
                          <span className={`inline-block px-3 py-1 rounded-full text-sm font-bold ${getRatioColor(unknownEmployee.ratio)}`}>
                            {unknownEmployee.ratio}%
                          </span>
                        </td>
                      </tr>
                    )}
                    
                    {/* Intervenants externes */}
                    {intervenants.map((emp, index) => (
                      <tr key={index} className="hover:bg-gray-50">
                        <td className="px-6 py-4">
                          <div className="font-medium text-gray-900">{emp.employee_name}</div>
                        </td>
                        <td className="px-6 py-4 text-center text-gray-600">{emp.nb_missions}</td>
                        <td className="px-6 py-4 text-right">
                          <div className="text-gray-900">{emp.total_paye.toFixed(2)}€</div>
                          <div className="text-xs text-gray-500">Prix Intervenant</div>
                        </td>
                        <td className="px-6 py-4 text-right text-gray-900">{emp.chiffre_genere.toFixed(2)}€</td>
                        <td className={`px-6 py-4 text-right font-bold ${getMargeColor(emp.marge)}`}>{emp.marge.toFixed(2)}€</td>
                        <td className="px-6 py-4 text-center">
                          <span className={`inline-block px-3 py-1 rounded-full text-sm font-bold ${getRatioColor(emp.ratio)}`}>
                            {emp.ratio}%
                          </span>
                        </td>
                      </tr>
                    ))}
                  </>
                );
              })()}
            </tbody>
          </table>
        </div>
      </div>

      {/* Tableau par logement */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6 overflow-hidden">
        <div className="bg-gradient-to-r from-blue-50 to-blue-100 px-6 py-4 border-b border-gray-200">
          <h2 className="text-xl font-bold text-gray-900">🏠 Top Logements par Marge (Groupés par Client)</h2>
        </div>
        
        {/* Légende des termes pour logements */}
        <div className="bg-green-50 px-6 py-3 border-b border-green-200">
          <div className="text-xs text-gray-700 space-y-1">
            <div className="font-semibold text-green-900 mb-2">📖 Légende des termes :</div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
              <div><span className="font-bold">Généré :</span> CA total du logement (ce que le client paie)</div>
              <div><span className="font-bold">Coût :</span> Total payé aux employés pour ce logement</div>
              <div><span className="font-bold">Marge :</span> Bénéfice du logement (Généré - Coût)</div>
            </div>
            <div className="mt-2 text-gray-600">💡 Les logements sont groupés par client et triés par marge décroissante</div>
          </div>
        </div>
        
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Client</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Logement</th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Missions</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Généré</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Coût</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Marge</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {(() => {
                // Trier par client puis par marge
                const sortedByClient = [...bilan.par_logement].sort((a, b) => {
                  const clientCompare = a.client_name.localeCompare(b.client_name, 'fr');
                  if (clientCompare !== 0) return clientCompare;
                  return b.marge - a.marge; // Marge décroissante au sein d'un même client
                });
                
                let lastClient = null;
                
                return sortedByClient.slice(0, 20).map((housing, index) => {
                  const isNewClient = housing.client_name !== lastClient;
                  lastClient = housing.client_name;
                  
                  return (
                    <tr 
                      key={index} 
                      className={`hover:bg-gray-50 ${isNewClient && index > 0 ? 'border-t-2 border-blue-300' : ''}`}
                    >
                      <td className={`px-6 py-4 ${isNewClient ? 'font-bold text-blue-900 bg-blue-50' : 'text-gray-400 text-sm'}`}>
                        {isNewClient ? housing.client_name : '↳'}
                      </td>
                      <td className="px-6 py-4 text-gray-900">
                        {housing.housing_name}
                      </td>
                      <td className="px-6 py-4 text-center text-gray-600">{housing.nb_missions}</td>
                      <td className="px-6 py-4 text-right text-gray-900">{housing.chiffre_genere}€</td>
                      <td className="px-6 py-4 text-right text-gray-600">{housing.cout_employe}€</td>
                      <td className={`px-6 py-4 text-right font-bold ${getMargeColor(housing.marge)}`}>
                        {housing.marge}€
                      </td>
                    </tr>
                  );
                });
              })()}
            </tbody>
          </table>
        </div>
      </div>

      {/* Tableau par client */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="bg-gradient-to-r from-green-50 to-green-100 px-6 py-4 border-b border-gray-200">
          <h2 className="text-xl font-bold text-gray-900">👤 Performance par Client (Ordre Alphabétique)</h2>
        </div>
        
        {/* Légende des termes pour clients */}
        <div className="bg-yellow-50 px-6 py-3 border-b border-yellow-200">
          <div className="text-xs text-gray-700 space-y-1">
            <div className="font-semibold text-yellow-900 mb-2">📖 Légende des termes :</div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
              <div><span className="font-bold">Facturé :</span> Total facturé au client (CA généré)</div>
              <div><span className="font-bold">Coût :</span> Total payé aux employés pour ce client</div>
              <div><span className="font-bold">Marge Totale :</span> Bénéfice total (Facturé - Coût)</div>
            </div>
            <div className="mt-2"><span className="font-bold">Marge Moyenne :</span> Marge moyenne par mission pour ce client</div>
          </div>
        </div>
        
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Client</th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Missions</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Facturé</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Coût</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Marge Totale</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Marge Moy.</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {[...bilan.par_client].sort((a, b) => 
                a.client_name.localeCompare(b.client_name, 'fr', { sensitivity: 'base' })
              ).map((client, index) => (
                <tr key={index} className="hover:bg-gray-50">
                  <td className="px-6 py-4 font-medium text-gray-900">{client.client_name}</td>
                  <td className="px-6 py-4 text-center text-gray-600">{client.nb_missions}</td>
                  <td className="px-6 py-4 text-right text-gray-900">{client.total_facture}€</td>
                  <td className="px-6 py-4 text-right text-gray-600">{client.cout_employe}€</td>
                  <td className={`px-6 py-4 text-right font-bold ${getMargeColor(client.marge)}`}>
                    {client.marge}€
                  </td>
                  <td className={`px-6 py-4 text-right ${getMargeColor(client.marge_moyenne)}`}>
                    {client.marge_moyenne}€
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default BilanRecap;
